/* eslint-disable react-hooks/exhaustive-deps */
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import classes from "../AnnotationJobData/AnnotationJobData.module.scss";
import {KTSVG} from "../../../../../../_metronic/helpers";
import AnnotationTag from "../AnnotationTag/AnnotationTag";
import {
    AnnotationClassesListResponse,
    AnnotationSegmentListResponse,
    AnnotationSegmentResponse,
    PlotNameListResponse,
    UpdateAnnotationRequest
} from "../../../../../../AUTO_GENERATED_TYPES";
import React, {useCallback, useEffect, useState} from "react";
import useAnnotation from "../../../../../../store/annotation/useAnnotation";
import QueryKeys from "../../../../../../react-query/QueryKeys";
import {useParams} from "react-router-dom";
import {useQueryClient} from "react-query";
import {Option} from "../../../../../../types/CommunitySortTypes";
import VideoModal from "../../../../../Components/UI/VideoModal";
import PlotlyOrImage from "../../../../../Components/UI/PlotlyOrImage";

// Detect Plotly JSON (supports presigned URLs with query params)
const isPlotlyJson = (url: string): boolean => {
    if (!url) return false;

    try {
        const parsed = new URL(url);
        if (parsed.pathname.toLowerCase().endsWith('.json')) {
            return true;
        }
    } catch {
        // fall through
    }

    const withoutQuery = url.split('?')[0].toLowerCase();
    return withoutQuery.endsWith('.json');
};

interface AnnotationZoomModalProps {
    showVideoGeneratorIcon: boolean
    zoomContext: { show: boolean, segment?: AnnotationSegmentResponse, index?: number }
    toggleZoom: (segment?: AnnotationSegmentResponse | React.KeyboardEventHandler<any> | React.MouseEventHandler<any> | undefined) => void
    openModalHandler: (segment?: AnnotationSegmentResponse) => void
    annotationClasses: AnnotationClassesListResponse | undefined
    tagHandler: (requestBody: UpdateAnnotationRequest[]) => void
    setZoomContext: React.Dispatch<React.SetStateAction<{ show: boolean, segment?: AnnotationSegmentResponse | undefined, index?: number | undefined }>>
    currPage?: number
    pages?: number
    changePage?: ( newPage: number ) => void;
}

const AnnotationZoomModal: React.FC<AnnotationZoomModalProps> = ({
                                                                    showVideoGeneratorIcon,
                                                                     zoomContext,
                                                                     toggleZoom,
                                                                     openModalHandler,
                                                                     annotationClasses,
                                                                     setZoomContext,
                                                                     tagHandler,
                                                                     currPage,
                                                                     pages,
                                                                     changePage
                                                                 }) => {
    const {id} = useParams()
    const annotationId = Number(id)
    const queryClient = useQueryClient()
    const [showVideoModal, setShowVideoModal] = useState(false)
    const segment = zoomContext.segment

    // Dynamic responsive plot width based on viewport (height is not constrained - let plot be natural)
    const [plotWidth, setPlotWidth] = useState(1100);

    useEffect(() => {
        const calculateWidth = () => {
            // Use 88% of viewport width for the plot
            const width = Math.floor(window.innerWidth * 0.88);
            setPlotWidth(width);
        };

        calculateWidth();
        window.addEventListener('resize', calculateWidth);
        
        return () => {
            window.removeEventListener('resize', calculateWidth);
        };
    }, []);

    const {
        data: {page, itemsPerPage, dataRequest, tagFilter, imageType},
        functions: {changeImageType}
    } = useAnnotation()


    const segments = (JSON.parse(JSON.stringify(queryClient.getQueryData([QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page]))) as AnnotationSegmentListResponse)?.segments
    const annotationIndex = segments?.findIndex((seg => seg.id === segment?.id))
    const segClasses = queryClient.getQueryData<AnnotationClassesListResponse>([QueryKeys.AnnotationJobClasses, annotationId])

    const changeImageTypeWithArrows = useCallback((type: 'ArrowUp' | 'ArrowDown') => {
        const types = queryClient.getQueryData<PlotNameListResponse>([QueryKeys.AnnotationPlotNames,annotationId])?.plot_names
        if (!types) {
            return
        }

        const typesArray = Object.entries(types)
        const currTypeIndex = typesArray.findIndex((value => value[0] === imageType.value))
        const nextIndex = currTypeIndex + (type === 'ArrowUp' ? -1 : 1)
        const newType = typesArray.at(nextIndex === typesArray.length ? 0 : nextIndex)
        if (!newType) {
            return
        }
        const newTypeAsObj = {value: newType[0], label: newType[1]} as Option
        changeImageType(newTypeAsObj)
    }, [queryClient, changeImageType])

    useEffect(() => {
        if (!zoomContext.show) {
            return
        }
        const handleKeyPress = (e: KeyboardEvent) => {

            e.preventDefault()
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                changeImageTypeWithArrows(e.key as 'ArrowUp' | 'ArrowDown')
            }
        }

        document.addEventListener('keydown', handleKeyPress)

        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }

    }, [changeImageTypeWithArrows, zoomContext.show])

    useEffect(() => {
        let handleNavigation: (event: KeyboardEvent) => void;
    
        if (segments && zoomContext.show) {
            const previous = segments[(annotationIndex ?? 0) - 1];
            const next = segments[(annotationIndex ?? 0) + 1];
    
            handleNavigation = async (event: KeyboardEvent) => {
                console.log('pages: ', currPage);
                if (event.key === 'ArrowRight') {
                    if (next) {
                        toggleZoom(next);
                    } else if (currPage && annotationIndex === segments.length - 1) {
                        const nextPage = currPage + 1;
                        if (changePage && pages && nextPage <= pages) {
                            setZoomContext((prevState) => ({ ...prevState, show: false }));
                            await changePage(nextPage);
                            await queryClient.invalidateQueries([
                                QueryKeys.Annotation,
                                QueryKeys.AnnotationJobData,
                                annotationId,
                                dataRequest,
                                tagFilter,
                                itemsPerPage,
                                nextPage,
                            ]);
                            const newSegments = queryClient.getQueryData<AnnotationSegmentListResponse>(
                                [QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, nextPage]
                            )?.segments;
                            if (newSegments && newSegments.length > 0) {
                                setZoomContext({
                                    show: true,
                                    segment: newSegments[0],
                                    index: 0,
                                });
                            }
                        }
                    }
                } else if (event.key === 'ArrowLeft') {
                    if (previous) {
                        toggleZoom(previous);
                    } else if (currPage && annotationIndex === 0 && currPage > 1) {
                        const prevPage = currPage - 1;
                        if (prevPage >= 1 && changePage) {
                            setZoomContext((prevState) => ({ ...prevState, show: false }));
                            await changePage(prevPage);
                            await queryClient.invalidateQueries([
                                QueryKeys.Annotation,
                                QueryKeys.AnnotationJobData,
                                annotationId,
                                dataRequest,
                                tagFilter,
                                itemsPerPage,
                                prevPage,
                            ]);
                            const newSegments = queryClient.getQueryData<AnnotationSegmentListResponse>(
                                [QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, prevPage]
                            )?.segments;
                            if (newSegments && newSegments.length > 0) {
                                setZoomContext({
                                    show: true,
                                    segment: newSegments[newSegments.length - 1],
                                    index: newSegments.length - 1,
                                });
                            }
                        }
                    }
                }
            };
            document.addEventListener('keydown', handleNavigation);
        }
    
        return () => {
            document.removeEventListener('keydown', handleNavigation);
        };
    }, [annotationIndex, segments, toggleZoom, queryClient, annotationId, dataRequest, tagFilter, itemsPerPage, zoomContext.show]);
        

    const openDetailsHandler = ()=>{
        toggleZoom()
        setTimeout(()=>{
            openModalHandler(segment)
        },500)
    }


    const extractVideoSegmentPlotDatetime = (segment: AnnotationSegmentResponse | undefined) => {
        if (segment) {
            let creation_time = '';
            const plotUrl = segment.plots?.['path_2d_motion_plot'];
            if (plotUrl) {
                // Extract the datetime part between '/plots/' and the next '/'
                const match = plotUrl.match(/plots\/([^/]+)\//);
                if (match && match[1]) {
                    creation_time = match[1];
                }
            }
            return creation_time
        }
        return ''
    }
    
    const videoSegmentPlotDatetime = extractVideoSegmentPlotDatetime(segment)

    const generateVideo = () => {
        setShowVideoModal(true)
    }

    // Determine plot source and type outside render for conditional modal styling
    const plotSrc = (segment?.plots ? segment?.plots[imageType.value as keyof typeof segment] : Object.values(segment?.plots ?? {})[0]) as string;
    const isPlotly = isPlotlyJson(plotSrc);

    return <Modal isOpen={zoomContext.show}
                  toggle={toggleZoom as any}
                  centered
                  scrollable={isPlotly}
                  style={{width: "max-content", maxWidth: "none"}}
                  className={'position-relative'}>
        <ModalHeader toggle={toggleZoom as any}>
            Id: {segment?.id}
            <div
                className={classes.page}>{zoomContext?.index !== undefined ? zoomContext?.index + 1 : 0} / {itemsPerPage} -
                Page {page}</div>

        </ModalHeader>
        <ModalBody style={{
            width: "auto", 
            maxWidth: "none", 
            ...(isPlotly && { maxHeight: "85vh", overflow: "auto" })
        }}>
            <div className={`${classes.images__image_container} ${classes['images__image_container--zoom']}`}>
                <PlotlyOrImage
                    src={plotSrc}
                    alt="segment_graph"
                    width={isPlotly ? plotWidth : undefined}
                />
                <div className={classes.images__tagging}>
                    {annotationClasses?.classes.map((annotationClass, index) =>
                        <AnnotationTag key={index} annotationClass={annotationClass}
                                       segId={segment?.id}
                                       isSelected={segment?.tag === annotationClass.class_code}
                                       onClick={(requestBody: UpdateAnnotationRequest[]) => {
                                           tagHandler(requestBody)
                                           if (requestBody[0].seg_id_list[0] === segment?.id) {
                                               setZoomContext(prevState => {
                                                   const newSeg = JSON.parse(JSON.stringify(prevState.segment)) as AnnotationSegmentResponse
                                                   const newTag = requestBody[0].segment_tag
                                                   newSeg.tag = newTag

                                                   const oldName = newSeg.tag_name
                                                   newSeg.tag_name = segClasses?.classes.find(segClass => segClass.class_code === newTag)?.class_name ?? oldName
                                                   return {...prevState, segment: newSeg}
                                               })
                                           }
                                       }}/>)}
                    <span onClick={openDetailsHandler} className={classes.mag_glass}
                    >
                                <KTSVG
                                    path='/media/icons/duotune/general/gen004.svg'
                                    className={'svg-icon-edit svg-icon-2qx cursor-pointer'}
                                />
                            </span>
                    <span style={{ marginLeft: '8px', display: showVideoGeneratorIcon ? 'block' : 'none' }} onClick={generateVideo} className={classes.mag_glass}
                    >
                        <KTSVG
                            path='/media/icons/duotune/general/gen063.svg'
                            className={'svg-icon-edit svg-icon-2qx cursor-pointer'}
                        />
                    </span>
                    <VideoModal
                        show={showVideoModal}
                        onClose={() => setShowVideoModal(false)}
                        annotationId={annotationId}
                        segmentId={segment?.seg_id.toString() ?? ''}
                        creationTime={videoSegmentPlotDatetime}
                        appToken=''
                        scaleFactor={0.5}
                        screenWidth={segment?.screen_width ?? 0}
                        screenHeight={segment?.screen_height ?? 0}
                    />

                </div>
            </div>
        </ModalBody>
    </Modal>
}

export default AnnotationZoomModal
